/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { Dropdown, Menu, Typography } from 'antd';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import styles from './FilterDropdown.less';

const { Text } = Typography;

/** arco 位置码 → antd placement（调用方沿用原型的 `position` 写法） */
const PLACEMENT = {
  top: 'top',
  tl: 'topLeft',
  tr: 'topRight',
  bottom: 'bottom',
  bl: 'bottomLeft',
  br: 'bottomRight',
  left: 'left',
  lt: 'leftTop',
  lb: 'leftBottom',
  right: 'right',
  rt: 'rightTop',
  rb: 'rightBottom',
};

/**
 * FilterDropdown —— 统一的维度切换下拉（本模块 antd Dropdown 的唯一落地点）
 * ------------------------------------------------------------------
 * 后台原来的维度切换很散：Segmented 切「月/日」、Radio 切订单类型、几处
 * Dropdown 各自为政。这里统一成一种控件，用于
 *   · 趋势切换（月 / 日）
 *   · 时间范围（最近 7 天 / 最近 30 天 / 最近一年 / 自定义）
 *   · 订单类型（全部 / 月卡 / 年卡 / pro或指导会员 / 指导课 / 训练计划）
 *   · 会员类型、完课指标切换、LITTA POWER 教练列表 ……
 *
 * options 用 group 字段分组，内部渲染成 Menu.ItemGroup；
 * 传 renderPanel 可换成完全自定义面板（例如内嵌 RangePicker），此时不会
 * 因为点击面板而关闭下拉。
 *
 * @param label      触发器左侧的维度名（灰色），如「时间范围」
 * @param value      当前值（单选为 string，多选为数组）
 * @param options    选项数组：value label group desc disabled
 * @param onChange   回调：入参为 value
 * @param multiple   多选模式（菜单不关闭，展示勾选态）
 * @param renderPanel 自定义面板：返回 ReactNode
 * @param trigger    'click' | 'hover'，默认 click
 * @param position   位置码（沿用原型写法，如 bl/br），默认 bl
 * @param icon       触发器左侧图标
 * @param showValue  触发器里是否展示当前值文案，默认 true
 * @param width      触发器最小宽度
 */
const FilterDropdown = (props) => {
  const {
    label,
    value,
    options = [],
    onChange,
    multiple = false,
    renderPanel,
    trigger = 'click',
    position = 'bl',
    icon,
    showValue = true,
    width,
    disabled,
    className,
    popupClassName,
  } = props;

  const selectedKeys = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    return value === undefined || value === null ? [] : [String(value)];
  }, [value, multiple]);

  // antd Dropdown 没有 arco 的 clickToClose，改成自己受控；
  // 否则多选/内嵌面板会被一次点击关掉
  const [open, setOpen] = useState(false);

  const currentLabel = useMemo(() => {
    const flat = [];
    options.forEach((o) => flat.push(o));
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length) return '全部';
      const names = arr
        .map(
          (v) => (flat.find((o) => String(o.value) === String(v)) || {}).label,
        )
        .filter(Boolean);
      return names.join('、');
    }
    const hit = flat.find((o) => String(o.value) === String(value));
    return hit ? hit.label : '';
  }, [options, value, multiple]);

  const handleClickMenuItem = (key) => {
    if (!multiple) {
      if (String(key) !== String(value)) onChange && onChange(key);
      setOpen(false); // 单选：选完就关
      return;
    }
    const arr = Array.isArray(value) ? [...value] : [];
    const idx = arr.findIndex((v) => String(v) === String(key));
    if (idx > -1) arr.splice(idx, 1);
    else arr.push(key);
    onChange && onChange(arr);
  };

  const overlay = renderPanel ? (
    <div className={styles.panel}>{renderPanel()}</div>
  ) : (
    <Menu
      className={styles.menu}
      selectedKeys={selectedKeys}
      onClick={({ key }) => handleClickMenuItem(key)}
    >
      {renderOptions(options, multiple)}
    </Menu>
  );

  return (
    <Dropdown
      overlay={overlay}
      placement={PLACEMENT[position] || 'bottomLeft'}
      trigger={[trigger]}
      disabled={disabled}
      visible={open}
      onVisibleChange={setOpen}
      overlayClassName={popupClassName}
      autoAdjustOverflow
    >
      <span
        className={[
          styles.trigger,
          open ? styles.open : '',
          className || '',
        ].join(' ')}
        style={width ? { minWidth: width } : undefined}
      >
        {icon && <span className={styles.triggerIcon}>{icon}</span>}
        {label && <Text className={styles.label}>{label}</Text>}
        {showValue && currentLabel && (
          /* 不要用 Text 的 ellipsis={{rows:1}}：它会加 display:-webkit-box，
             把 trigger 的 flex 交叉轴居中破坏掉，文字会顶出框外（实测 offsetTop:-3） */
          <Text className={styles.value} title={currentLabel}>
            {currentLabel}
          </Text>
        )}
        <DownOutlined className={styles.caret} />
      </span>
    </Dropdown>
  );
};

/** options -> Menu 节点，按 group 聚合 */
function renderOptions(options, multiple) {
  const nodes = [];
  let currentGroup = null;
  let groupBuffer = [];

  const flush = () => {
    if (!groupBuffer.length) return;
    const key = currentGroup || `__default_${nodes.length}`;
    if (currentGroup) {
      nodes.push(
        <Menu.ItemGroup key={key} title={currentGroup}>
          {groupBuffer}
        </Menu.ItemGroup>,
      );
    } else {
      groupBuffer.forEach((n) => nodes.push(n));
    }
    groupBuffer = [];
  };

  options.forEach((o) => {
    if ((o.group || null) !== currentGroup) {
      flush();
      currentGroup = o.group || null;
    }
    groupBuffer.push(
      <Menu.Item key={String(o.value)} disabled={o.disabled}>
        <span className={styles.item}>
          <span className={styles.itemMain}>
            <span className={styles.itemLabel}>{o.label}</span>
            {o.desc && <span className={styles.itemDesc}>{o.desc}</span>}
          </span>
          {multiple && (
            <CheckOutlined className={`${styles.check} ${styles.hidden}`} />
          )}
        </span>
      </Menu.Item>,
    );
  });
  flush();
  return nodes;
}

export default FilterDropdown;
